import { useCallback, useEffect } from 'react';
import { useAuthContext } from '../auth/useAuthContext';
import { useEditorContext } from './useEditorContext';
import { dataLayerGA } from '../../services/analytics/dataLayerGa';
import { useTrackFreeSmartGeneration } from '../query/useTrackFreeSmartGeneration';
import { useEditorTextState } from './useEditorTextState';
import { useGenerateFirstHomeworkHelp } from '../query/useGenerateFirstHomeworkHelp';
import { useGenerateNextHomeworkHelp } from '../query/useGenerateNextHomeworkHelp';
import { useGenerateTitleHomeworkHelp } from '../query/useGenerateTitleHomeworkHelp';
import { StorageKey, StorageType } from '../../utilities/constants/common';
import { getStorage } from '../../utilities/helpers/storage';
import { useError } from '../state/useError';

export const useEditorGeneration = () => {
  const {
    freePremiumGenerationsAttemptsCount,
    authDispatch,
    AuthActionCreator,
  } = useAuthContext();

  const { generationTitle, generationChatId, generationSubjectGroup } =
    useEditorContext();
  const {
    isTextGenerating: isFirstTextGenerating,
    isTextGenerationSuccess: isFirstTextGenerationSuccess,
    generateFirstHomeworkHelpMutation,
  } = useGenerateFirstHomeworkHelp();
  const {
    isTextGenerating: isNextTextGenerating,
    isTextGenerationSuccess: isNextTextGenerationSuccess,
    generateNextHomeworkHelpMutation,
  } = useGenerateNextHomeworkHelp();
  const { generateTitleHomeworkHelpMutation } = useGenerateTitleHomeworkHelp();

  const { trackFreeSmartGen } = useTrackFreeSmartGeneration();
  const { text, setText } = useEditorTextState();
  const { error } = useError();

  const isTextGenerating = isFirstTextGenerating || isNextTextGenerating;
  const isTextGenerationSuccess =
    isFirstTextGenerationSuccess || isNextTextGenerationSuccess;

  const handleGenerationRequest = useCallback(
    (currentRequest?: string) => {
      if (generationChatId) {
        setText('');
        generateNextHomeworkHelpMutation(currentRequest);
      } else {
        generateFirstHomeworkHelpMutation();
        generateTitleHomeworkHelpMutation();
      }

      dataLayerGA.submitFormSuccess();
    },
    [
      generationChatId,
      setText,
      generateFirstHomeworkHelpMutation,
      generateNextHomeworkHelpMutation,
      generateTitleHomeworkHelpMutation,
    ]
  );

  useEffect(() => {
    if (isTextGenerationSuccess) {
      if (freePremiumGenerationsAttemptsCount > 0) {
        const attemptsLeftCount = freePremiumGenerationsAttemptsCount - 1;
        authDispatch(
          AuthActionCreator.setFreePremiumGenerationsAttemptsCount(
            attemptsLeftCount
          )
        );
        trackFreeSmartGen();
        getStorage(StorageType.LOCAL).setItem(
          StorageKey.JUST_USED_FREE_SMART_ANSWER,
          'true'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTextGenerationSuccess, trackFreeSmartGen]);

  return {
    text,
    title: generationTitle,
    subjectGroup: generationSubjectGroup,
    error,
    isTextGenerating,
    isTextGenerationSuccess,
    handleGenerationRequest,
  };
};
